import { useCallback, useMemo } from "react";
import { useTypedParams } from "react-router-typesafe-routes";
import { useDipBatchChangeRequestsJobs } from "~/features/dataImportProjects/hooks/useDipBatchChangeRequestsJobs";
import { useDipBatchEvidencesJobs } from "~/features/dataImportProjects/hooks/useDipBatchEvidencesJobs";
import { useDipDataSourceStatsSummary } from "~/features/dataImportProjects/hooks/useDipDataSourceStatsSummary";
import { useDipDetail } from "~/features/dataImportProjects/hooks/useDipDetail";
import { useDipEvidenceCount } from "~/features/dataImportProjects/hooks/useDipEvidenceCount";
import { useDipRecordComposers } from "~/features/dataImportProjects/hooks/useDipRecordComposers";
import { toOutputSummary, toProjectInfo, toSchemaGraph } from "~/features/dataImportProjects/utils/transformers";
import { v2Route } from "~/v2/routes";
import { DataImportProjectPipelinePresenter } from "./DataImportProjectPipelinePresenter";

export function DataImportProjectPipelineContainer() {
  const { id = "" } = useTypedParams(v2Route.dataImportProjects.detail);
  const { data: detail, refetch: refetchDetail } = useDipDetail(id);
  const { data: bcrJobs, refetch: refetchBcrJobs } = useDipBatchChangeRequestsJobs(id);
  const { data: evidence, refetch: refetchEvidence } = useDipEvidenceCount(id);
  const { data: composers, refetch: refetchComposers } = useDipRecordComposers(id);
  const { data: bej, refetch: refetchBej } = useDipBatchEvidencesJobs(id);
  const project = detail.dataImportProjectsByPk;
  const dataSourceIds = useMemo(
    () =>
      [
        ...new Set(
          (project?.dataSourceImportProjects ?? [])
            .filter(({ dataSource }) => dataSource.deactivatedAt == null)
            .map(({ dataSource }) => dataSource.id),
        ),
      ].sort(),
    [project?.dataSourceImportProjects],
  );
  const { summary: dataSourceStats, refetch: refetchDataSourceStats } = useDipDataSourceStatsSummary(dataSourceIds);

  // Refetch every pipeline query so the overview reflects the latest state without a full reload.
  const onRefresh = useCallback(
    () =>
      Promise.all([
        refetchDetail(),
        refetchBcrJobs(),
        refetchEvidence(),
        refetchComposers(),
        refetchBej(),
        refetchDataSourceStats(),
      ]),
    [refetchDetail, refetchBcrJobs, refetchEvidence, refetchComposers, refetchBej, refetchDataSourceStats],
  );

  if (!project) return null;

  const batchChangeRequestsJobs = bcrJobs.batchChangeRequestsJobs;
  const activeComposers = composers.recordComposerDefinitions.filter((composer) => composer.deactivatedAt == null);
  const evidenceCount = evidence.evidencesAggregate.aggregate?.count ?? 0;

  return (
    <DataImportProjectPipelinePresenter
      projectInfo={toProjectInfo(project)}
      summaryGroups={toOutputSummary(
        batchChangeRequestsJobs,
        bej.batchEvidencesJobs,
        evidenceCount,
        dataSourceStats,
        id,
      )}
      dataImportProjectId={id}
      batchChangeRequestsJobs={batchChangeRequestsJobs}
      composers={composers.recordComposerDefinitions}
      batchEvidencesJobs={bej.batchEvidencesJobs}
      projectDataSources={project.dataSourceImportProjects}
      schema={toSchemaGraph(activeComposers, id)}
      onRefresh={onRefresh}
    />
  );
}
